import React, { useState, useEffect } from "react";
import Datetime from "react-datetime";
import AlertCard from "../cards/AlertCard";
import { showErrorToast, showPromiseToast } from "../../services/toastService";
import { useLocation, useNavigate } from "react-router-dom";
import { createAlert, getAlertById, updateAlert } from "../../services/alertsService";

const AlertCreate = ({ setIsDirty }) => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const alertId = queryParams.get("id");
	const navigate = useNavigate();
	const dateFormatter = (date) => date.toISOString().slice(0, 19).replace("T", " ");

	const emptyAlert = {
		id: null,
		title: "",
		description: "",
		category_name: "",
		area_name: "",
		priority: "low",
		valid_date: dateFormatter(new Date()),
		expire_date: dateFormatter(new Date(new Date().setDate(new Date().getDate() + 3))),
	};

	const [formData, setFormData] = useState(emptyAlert);

	useEffect(() => {
		if (!alertId) return;
		getAlertById(alertId).then((response) => {
			const alert = response.data;
			setFormData({
				id: alert.id || null,
				title: alert.title || "",
				description: alert.description || "",
				category_name: alert.category_name || "",
				area_name: alert.area_name || "",
				priority: alert.priority || "low",
				valid_date: alert.valid_date
					? dateFormatter(new Date(alert.valid_date))
					: dateFormatter(new Date()),
				expire_date: alert.expire_date
					? dateFormatter(new Date(alert.expire_date))
					: dateFormatter(new Date(new Date().setDate(new Date().getDate() + 3))),
			});
		});
	}, [alertId]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		setIsDirty(true);
	};

	const handleDateChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: dateFormatter(value) }));
		setIsDirty(true);
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if (
			!formData.title ||
			!formData.valid_date ||
			!formData.expire_date ||
			!formData.category_name
		) {
			showErrorToast("Per proseguire compilare i campi obbligatori.");
			return;
		}

		if (formData.id)
			showPromiseToast(updateAlert(formData), {
				promise: "Aggiornamento in corso...",
				success: "Allerta aggiornata con successo",
			}).then(() => {
				setIsDirty(false);
				setFormData(emptyAlert);
				navigate(`?tab=new-alert`);
			});
		else
			showPromiseToast(createAlert(formData), {
				promise: "Creazione in corso...",
				success: "Allerta creata con successo",
			}).then(() => {
				setIsDirty(false);
				setFormData(emptyAlert);
				navigate(`?tab=alerts`);
			});
	};

	return (
		<div className="bg-white shadow-md rounded-lg p-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
			<div>
				<h2 className="text-xl font-bold mb-4 text-gray-800">
					{alertId ? "Modifica Allerta" : "Crea Nuova Allerta"}
				</h2>
				<form onSubmit={handleSubmit} className="space-y-4">
					{/* Titolo */}
					<div>
						<label className="block text-sm font-medium text-gray-700">Titolo*</label>
						<input
							type="text"
							name="title"
							placeholder="Titolo dell'allerta"
							value={formData.title}
							onChange={handleChange}
							className="w-full border border-gray-300 rounded-md p-2"
						/>
					</div>

					{/* Descrizione */}
					<div>
						<label className="block text-sm font-medium text-gray-700">
							Descrizione
						</label>
						<textarea
							name="description"
							placeholder="Descrizione dell'allerta"
							value={formData.description}
							onChange={handleChange}
							className="w-full border border-gray-300 rounded-md p-2"
						/>
					</div>

					{/* Categoria */}
					<div>
						<label className="block text-sm font-medium text-gray-700">
							Categoria*
						</label>
						<input
							type="text"
							name="category_name"
							placeholder="Categoria dell'allerta"
							value={formData.category_name}
							onChange={handleChange}
							className="w-full border border-gray-300 rounded-md p-2"
						/>
					</div>

					{/* Area */}
					<div>
						<label className="block text-sm font-medium text-gray-700">Area</label>
						<input
							type="text"
							name="area_name"
							placeholder="Area interessata"
							value={formData.area_name}
							onChange={handleChange}
							className="w-full border border-gray-300 rounded-md p-2"
						/>
					</div>

					{/* Priorità */}
					<div>
						<label className="block text-sm font-medium text-gray-700">Priorità</label>
						<select
							name="priority"
							value={formData.priority}
							onChange={handleChange}
							className="w-full border border-gray-300 rounded-md p-2"
						>
							<option value="low">Bassa</option>
							<option value="medium">Media</option>
							<option value="high">Alta</option>
						</select>
					</div>

					{/* Data e Ora di inizio */}
					<div>
						<label className="block text-sm font-medium text-gray-700">
							Data e Ora di inizio*
						</label>
						<Datetime
							value={new Date(formData.valid_date)}
							onChange={(value) => handleDateChange("valid_date", value)}
							dateFormat="YYYY-MM-DD"
							timeFormat="HH:mm"
							className="border border-gray-300 rounded-md w-full"
							inputProps={{
								placeholder: "Seleziona data e ora di inizio",
								className: "p-2 rounded-md w-full",
							}}
						/>
					</div>

					{/* Data e Ora di scadenza */}
					<div>
						<label className="block text-sm font-medium text-gray-700">
							Data e Ora di scadenza*
						</label>
						<Datetime
							value={new Date(formData.expire_date)}
							onChange={(value) => handleDateChange("expire_date", value)}
							dateFormat="YYYY-MM-DD"
							timeFormat="HH:mm"
							className="border border-gray-300 rounded-md w-full"
							inputProps={{
								placeholder: "Seleziona data e ora di termine",
								className: "p-2 rounded-md w-full",
							}}
						/>
					</div>

					{/* Pulsanti */}
					<div className="flex justify-end space-x-4">
						<button
							id="save"
							aria-label="Salva"
							type="submit"
							className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
						>
							{alertId ? "Modifica" : "Crea"}
						</button>
					</div>
				</form>
			</div>
			<div className="hidden xl:flex flex-col items-center border border-gray-300 rounded-lg p-4">
				<p className="w-full text-center text-lg font-semibold mb-4">
					Anteprima Card Allerta
				</p>
				{formData.title && formData.category_name && <AlertCard alert={formData} />}
			</div>
		</div>
	);
};

export default AlertCreate;
